import type { ReactNode } from 'react'
import AutoAwesomeMosaicRoundedIcon from '@mui/icons-material/AutoAwesomeMosaicRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import WorkspacesRoundedIcon from '@mui/icons-material/WorkspacesRounded'
import type { TemplateCategory, TemplateRegistryEntry } from '../../types/templates'
import { Checkbox, StatusChip } from '../Common'
import '../exploreHub.css'

export const CATEGORY_ICONS: Record<TemplateCategory, ReactNode> = {
  Workspaces: <WorkspacesRoundedIcon />,
  Designs: <DesignServicesRoundedIcon />,
  Automations: <HubRoundedIcon />,
  Forms: <DescriptionRoundedIcon />,
  Checklists: <FactCheckRoundedIcon />,
}

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])
const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback)

const FormsPreview = ({ payload }: { payload: Record<string, unknown> }) => {
  const fields = asArray<Record<string, unknown>>(payload.fields)
  const sections = asArray<string>(payload.sections)
  const grouped = sections.length > 0
    ? sections.map((title, idx) => ({
      title,
      fields: fields.slice(
        Math.floor((idx * fields.length) / sections.length),
        Math.floor(((idx + 1) * fields.length) / sections.length),
      ),
    }))
    : [{ title: 'Fields', fields }]
  return (
    <div className="templatePreviewForm">
      {grouped.map((group) => (
        <fieldset key={group.title}>
          <legend>{group.title}</legend>
          {group.fields.map((field, idx) => (
            <label key={String(field.id ?? idx)} data-required={field.required ? 'true' : 'false'}>
              <span>{asString(field.label, `Field ${idx + 1}`)}{field.required ? ' *' : ''}</span>
              <em>{asString(field.type, 'text')}</em>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

const DesignPreview = ({ payload }: { payload: Record<string, unknown> }) => {
  const lanes = asArray<string>(payload.lanes)
  const nodes = asArray<Record<string, unknown>>(payload.nodes)
  const connectors = asArray<Record<string, unknown>>(payload.connectors)
  const xs = nodes.map((n) => Number(n.x) || 0)
  const ys = nodes.map((n) => Number(n.y) || 0)
  const minX = Math.min(0, ...xs)
  const maxX = Math.max(1200, ...xs) + 160
  const minY = Math.min(0, ...ys)
  const maxY = Math.max(320, ...ys) + 80
  const nodeById = new Map(nodes.map((n) => [String(n.id ?? ''), n]))
  return (
    <div className="templatePreviewDesign">
      <div className="templatePreviewDesignLanes">
        {lanes.map((lane) => <span key={lane}>{lane}</span>)}
      </div>
      <svg viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        {connectors.map((c, idx) => {
          const from = nodeById.get(String(c.from ?? ''))
          const to = nodeById.get(String(c.to ?? ''))
          if (!from || !to) return null
          return (
            <line
              key={idx}
              x1={Number(from.x) + 60}
              y1={Number(from.y) + 20}
              x2={Number(to.x) + 60}
              y2={Number(to.y) + 20}
              stroke="currentColor"
              strokeWidth={2}
              strokeOpacity={0.4}
              markerEnd="url(#tp-arrow)"
            />
          )
        })}
        <defs>
          <marker id="tp-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
          </marker>
        </defs>
        {nodes.map((node) => {
          const isDecision = String(node.type ?? 'stage') === 'decision'
          const x = Number(node.x) || 0
          const y = Number(node.y) || 0
          return (
            <g key={String(node.id)}>
              {isDecision ? (
                <polygon points={`${x + 60},${y - 8} ${x + 128},${y + 20} ${x + 60},${y + 48} ${x - 8},${y + 20}`} fill="color-mix(in srgb, var(--accent-primary) 18%, transparent)" stroke="currentColor" />
              ) : (
                <rect x={x} y={y} width={120} height={40} rx={8} fill="color-mix(in srgb, var(--muted) 14%, transparent)" stroke="currentColor" />
              )}
              <text x={x + 60} y={y + 24} fontSize="12" fill="currentColor" textAnchor="middle">
                {String(node.label ?? '')}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

const AutomationPreview = ({ payload }: { payload: Record<string, unknown> }) => {
  const graph = (payload.graph && typeof payload.graph === 'object'
    ? (payload.graph as Record<string, unknown>)
    : null)
  if (!graph) {
    const trigger = payload.trigger as Record<string, unknown> | undefined
    const actions = asArray<Record<string, unknown>>(payload.actions)
    return (
      <div className="templatePreviewAutomation">
        <div className="templatePreviewFlowRow"><StatusChip size="sm" tone="info">{asString(trigger?.type, 'Trigger')}</StatusChip></div>
        <div className="templatePreviewFlowArrow">↓</div>
        <div className="templatePreviewFlowRow">
          {actions.map((action, idx) => (
            <StatusChip key={idx} size="sm" tone="neutral">{asString(action.type, `Action ${idx + 1}`)}</StatusChip>
          ))}
        </div>
      </div>
    )
  }
  const triggers = asArray<Record<string, unknown>>(graph.triggers)
  const constraint = graph.constraint as Record<string, unknown> | undefined
  const ifClause = graph.if as Record<string, unknown> | undefined
  const thens = asArray<Record<string, unknown>>(graph.then)
  const elses = asArray<Record<string, unknown>>(graph.else)
  const rate = constraint?.rate as Record<string, unknown> | undefined
  return (
    <div className="templatePreviewAutomation">
      <div className="templatePreviewFlowRow">
        {triggers.map((t, idx) => (
          <StatusChip key={idx} size="sm" tone="info">{asString(t.type, 'trigger')}</StatusChip>
        ))}
      </div>
      {Boolean(rate || constraint?.quota || constraint?.filter) && (
        <>
          <div className="templatePreviewFlowArrow">↓</div>
          <div className="templatePreviewFlowRow">
            <StatusChip size="sm" tone="warning">
              {rate ? `≤ ${String(rate.maxPerWindow)} / ${Math.round(Number(rate.windowMs) / 3_600_000)}h` : 'Guardrail'}
            </StatusChip>
          </div>
        </>
      )}
      <div className="templatePreviewFlowArrow">↓</div>
      {ifClause ? (
        <div className="templatePreviewFlowBranches">
          <div>
            <strong>THEN</strong>
            {thens.map((a, idx) => <StatusChip key={idx} size="sm" tone="neutral">{asString(a.type, `action ${idx + 1}`)}</StatusChip>)}
          </div>
          <div>
            <strong>ELSE</strong>
            {elses.map((a, idx) => <StatusChip key={idx} size="sm" tone="muted">{asString(a.type, `action ${idx + 1}`)}</StatusChip>)}
          </div>
        </div>
      ) : (
        <div className="templatePreviewFlowRow">
          {thens.map((a, idx) => <StatusChip key={idx} size="sm" tone="neutral">{asString(a.type, `action ${idx + 1}`)}</StatusChip>)}
        </div>
      )}
    </div>
  )
}

const ChecklistPreview = ({ payload }: { payload: Record<string, unknown> }) => {
  const groups = asArray<Record<string, unknown>>(payload.groups)
  return (
    <div className="templatePreviewChecklist">
      {groups.map((group) => (
        <section key={asString(group.title)}>
          <h4>{asString(group.title)}</h4>
          {asArray<string>(group.items).map((item) => (
            <Checkbox key={item} disabled label={item} />
          ))}
        </section>
      ))}
    </div>
  )
}

const CategoryPreviewBody = ({ template }: { template: TemplateRegistryEntry }) => {
  const payload = (template.previewData.payload ?? {}) as Record<string, unknown>
  switch (template.category) {
    case 'Forms': return <FormsPreview payload={payload} />
    case 'Designs': return <DesignPreview payload={payload} />
    case 'Automations': return <AutomationPreview payload={payload} />
    case 'Checklists': return <ChecklistPreview payload={payload} />
    default: return null
  }
}

export const PreviewRenderer = ({ template }: { template: TemplateRegistryEntry | null }) => {
  if (!template) {
    return (
      <div className="templateFactoryEmptyPreview">
        <AutoAwesomeMosaicRoundedIcon />
        <strong>Select a template</strong>
        <span>A live preview of fields, flow, design, or checklist appears here.</span>
      </div>
    )
  }
  return (
    <div className="templateFactoryPreview">
      <header>
        <span>{template.category} · v{template.registryVersion}</span>
        <h2>{template.previewData.title}</h2>
        <p>{template.previewData.summary || template.description}</p>
      </header>
      {template.previewData.metrics && template.previewData.metrics.length > 0 && (
        <div className="templateFactoryMetrics">
          {template.previewData.metrics.map((metric) => (
            <div key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>
          ))}
        </div>
      )}
      <section className="templatePreviewCanvas" aria-label={`${template.category} preview`}>
        <CategoryPreviewBody template={template} />
      </section>
      {(template.previewData.sections ?? []).map((section) => (
        <section key={section.title}>
          <h3>{section.title}</h3>
          <ul>{section.items.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      ))}
    </div>
  )
}
